import GeneratorPage from "@/pages/GeneratorPage";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Outlet, useLocation } from "react-router-dom";
import type { RouteRecord } from "vite-react-ssg";
import { MotionConfig } from "framer-motion";
import { Analytics } from "@vercel/analytics/react";
import { PostHogProvider } from "@posthog/react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { track, posthog } from "@/lib/analytics";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Success from "./pages/Success";
import MentionsLegales from "./pages/MentionsLegales";
import Confidentialite from "./pages/Confidentialite";
import Blog from "./pages/Blog";
import BlogPost from "./pages/BlogPost";
import ApplicationCoachSportif from "./pages/services/ApplicationCoachSportif";
import LogicielCoachSportif from "./pages/services/LogicielCoachSportif";
import { BLOG_ARTICLES } from "./content/blog";
import DeleteAccount from "./pages/DeleteAccount";

const queryClient = new QueryClient();

const RouteAnalytics = () => {
  const location = useLocation();

  useEffect(() => {
    track("$pageview", {
      path: location.pathname,
      search: location.search,
      hash: location.hash,
    });

    if (location.pathname === "/") {
      track("landing_page_viewed");
    }
  }, [location.hash, location.pathname, location.search]);

  return null;
};

// Scroll vers l'ancre (#pricing…) après navigation. Le navigateur scrolle au
// premier paint, puis les sections au-dessus se chargent (images, lazy) et
// décalent la cible — on se recale donc plusieurs fois jusqu'à stabilisation.
const HashScroll = () => {
  const location = useLocation();
  useEffect(() => {
    if (!location.hash) return;
    const id = location.hash.slice(1);
    let cancelled = false;
    const delays = [0, 150, 400, 800, 1400];
    delays.forEach((d) =>
      setTimeout(() => {
        if (cancelled) return;
        document.getElementById(id)?.scrollIntoView({ block: "start", behavior: d === 0 ? "auto" : "smooth" });
      }, d)
    );
    return () => { cancelled = true; };
  }, [location.hash, location.pathname]);
  return null;
};

const Layout = () => {
  // Les toasts (portals) ne sont montés qu'après hydratation → pas de mismatch SSR
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  return (
    <MotionConfig reducedMotion="user">
    <Analytics />
    <PostHogProvider client={posthog}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          {mounted && (
            <>
              <Toaster />
              <Sonner />
            </>
          )}
          <RouteAnalytics />
          <HashScroll />
          <Outlet />
        </TooltipProvider>
      </QueryClientProvider>
    </PostHogProvider>
    </MotionConfig>
  );
};

export const routes: RouteRecord[] = [
  {
    path: "/",
    element: <Layout />,
    children: [
      { index: true, element: <Index /> },
      { path: "application-coach-sportif", element: <ApplicationCoachSportif /> },
      { path: "logiciel-coach-sportif", element: <LogicielCoachSportif /> },
      { path: "generateur-programme", element: <GeneratorPage /> },
      { path: "blog", element: <Blog /> },
      {
        path: "blog/:slug",
        element: <BlogPost />,
        getStaticPaths: () => BLOG_ARTICLES.map((a) => `/blog/${a.slug}`),
      },
      { path: "success", element: <Success /> },
      { path: "mentions-legales", element: <MentionsLegales /> },
      { path: "confidentialite", element: <Confidentialite /> },
      { path: "delete-account", element: <DeleteAccount /> },
      { path: "suppression-compte", element: <DeleteAccount /> },
      // Garder le catch-all "*" en dernier
      { path: "*", element: <NotFound /> },
    ],
  },
];
